/**
 * Ask the WebView to keep this origin's storage (`D194`).
 *
 * The persisted Query cache and the catalogue copy live in IndexedDB, which
 * the browser may evict under storage pressure; `navigator.storage.persist()`
 * asks it not to. Chromium grants it on engagement heuristics and is not
 * known to grant it inside an Android WebView at all — so the answer is
 * RECORDED, not relied on: nothing that must survive lives there (the outbox
 * and the session are in Preferences and secure storage, `D163`). What was
 * granted and how much is used is written to `localStorage` under
 * {@link STORAGE_REPORT_KEY} and logged once, so a device check can read it.
 */

export const STORAGE_REPORT_KEY = "mypocketgym.storage-report";

export interface StorageReport {
  /** Epoch ms of the check. */
  readonly at: number;
  /** Whether the origin's storage is persistent; `null` when unknown. */
  readonly persisted: boolean | null;
  readonly quotaBytes: number | null;
  readonly usageBytes: number | null;
}

/** What this needs of `navigator.storage`; absent on old engines. */
export interface StorageManagerLike {
  estimate?(): Promise<{ quota?: number; usage?: number }>;
  persist?(): Promise<boolean>;
  persisted?(): Promise<boolean>;
}

/** Ask once, and report. Never throws. */
export async function requestPersistentStorage(
  storage: StorageManagerLike | undefined = globalThis.navigator?.storage,
  write: (report: StorageReport) => void = writeReport,
  now: () => number = Date.now
): Promise<StorageReport> {
  let persisted: boolean | null = null;
  try {
    persisted = (await storage?.persisted?.()) ?? null;
    if (persisted === false) {
      persisted = (await storage?.persist?.()) ?? false;
    }
  } catch {
    persisted = null;
  }
  const estimate = await storage?.estimate?.().catch(() => undefined);
  const report: StorageReport = {
    at: now(),
    persisted,
    quotaBytes: estimate?.quota ?? null,
    usageBytes: estimate?.usage ?? null,
  };
  write(report);
  return report;
}

function writeReport(report: StorageReport): void {
  try {
    globalThis.localStorage?.setItem(
      STORAGE_REPORT_KEY,
      JSON.stringify(report)
    );
  } catch {
    // A storage that refuses a write is the report's own answer.
  }
  console.info("[data] storage", report);
}
